import type { Automation } from '@capture/shared';
import { SectionCard } from './SectionCard';
import { RoleScopePicker, roleLabel } from './RoleScopePicker';
import styles from './panel.module.css';

interface Props {
  automation: Automation;
  onChange: (next: Automation) => void;
  /** The tenant's REAL roles — the picker writes the stable slug and displays
   *  the name (rename-safe, same shape as BranchesSection/OverrideSection). */
  roles?: { slug: string; name: string }[];
}

function appliesToSummary(automation: Automation, roles: { slug: string; name: string }[]): string {
  const restricted = automation.appliesToRoles ?? [];
  return restricted.length
    ? restricted.map((t) => roleLabel(t, roles)).join(', ')
    : 'Everyone';
}

/**
 * Applies to → Roles (finish-promised-runtimes item 3). When roles are
 * chosen, every branch evaluates only for acting operators holding one of
 * them; operator-less evaluation sites (schedule/batch-gate/balance) skip
 * role-scoped rules — the publish gate warns when that leaves the rule with
 * no site at all.
 */
export function AppliesToSection({ automation, onChange, roles = [] }: Props) {
  return (
    <div data-testid="appliesto-section">
      <SectionCard title="Applies to" summary={appliesToSummary(automation, roles)} defaultOpen>
        <div className={styles.formGrid}>
          <span className={styles.fieldLabel} id="applies-to-roles-label">
            Roles
          </span>
          <RoleScopePicker
            value={automation.appliesToRoles}
            onChange={(next) => onChange({ ...automation, appliesToRoles: next })}
            roles={roles}
            radioName="applies-to"
            labelledBy="applies-to-roles-label"
            unrestrictedLabel="Everyone"
            selectTestId="applies-to-roles-select"
            selectAriaLabel="Roles this automation applies to"
          />
        </div>
      </SectionCard>
    </div>
  );
}
